import type { SkopaCategory } from "../types";

// Geometric normalization scale factors adjusted for optimized 1px stroke weight balance
const sizeScale: Record<SkopaCategory, number> = {
  default: 1.0,
  commercial: 1.0,
  military: 0.9,
  helicopter: 1.0,
  "uas-fixed-wing-jet": 1.0,
  "uas-fixed-wing-prop": 1.0,
  "uas-fixed-wing-generic": 1.0,
  "uas-multi-rotor": 1.0,
  "uas-generic": 1.0,
  usv: 1.0,
  "ship-civilian": 1.0,
  "ship-military": 1.0,
};

const aircraftIcons: Record<SkopaCategory, (scale: number) => string> = {
  default: (s) => `
  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="-2 -2 36 36">
    <g id="plane" transform="translate(16 16) scale(${s}) translate(-16 -16) translate(0 32) rotate(-90)">
      <path d="M0,0H32V32H0Z" fill="none"/>
      <path d="M21.048,12.651H26.49a2.758,2.758,0,0,1,0,5.515H21.048l-5.442,9.651H11.524l2.721-9.651H8.8L6.082,20.923H2l2.721-5.515L2,9.894H6.082L8.8,12.651h5.442L11.524,3h4.082l5.442,9.651"
        transform="translate(0.394 0.592)"
        fill="none" stroke="#FFFFFF" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.0"/>
    </g>
  </svg>
  `,
  commercial: (s) => `
  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="-2 -2 36 36">
    <g transform="translate(16 16) scale(${s}) translate(-16 -16)" fill="none" stroke="#FFFFFF" stroke-width="1.0" stroke-linecap="round" stroke-linejoin="round">
      <path d="M16 4 C17.5 4, 17.5 7, 17.5 12 L17.5 25 C17.5 27, 14.5 27, 14.5 25 L14.5 12 C14.5 7, 14.5 4, 16 4 Z"/>
      <path d="M14.5 13.5 L5 18 L5 19.5 L14.5 16.5 Z"/>
      <path d="M17.5 13.5 L27 18 L27 19.5 L17.5 16.5 Z"/>
      <path d="M14.5 24 L10 26 L10 27 L14.5 25.5 Z"/>
      <path d="M17.5 24 L22 26 L22 27 L17.5 25.5 Z"/>
    </g>
  </svg>
  `,
  military: (s) => `
  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="-2 -2 36 36">
    <g transform="translate(16 16) scale(${s}) translate(-16 -16)" fill="none" stroke="#FFFFFF" stroke-width="1.0" stroke-linecap="round" stroke-linejoin="round">
      <path d="M16 3 L18 10 L17.5 24 L19 26 L16 27 L13 26 L14.5 24 L14 10 Z"/>
      <path d="M14.2 11 L4 21 L4 22.5 L7 22 L14.4 17.5 Z"/>
      <path d="M17.8 11 L28 21 L28 22.5 L25 22 L17.6 17.5 Z"/>
      <line x1="13.5" y1="13" x2="14.3" y2="15"/>
      <line x1="18.5" y1="13" x2="17.7" y2="15"/>
    </g>
  </svg>
  `,
  helicopter: (s) => `
  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="-2 -2 36 36">
    <g transform="translate(16 16) scale(${s}) translate(-16 -16)" fill="none" stroke="#FFFFFF" stroke-width="1.0" stroke-linecap="round" stroke-linejoin="round">
      <line x1="4" y1="9" x2="28" y2="9"/>
      <line x1="16" y1="9" x2="16" y2="12"/>
      <path d="M16 12 C20 12, 21.5 15, 21.5 18 C21.5 21, 19.5 22, 16 22 C12.5 22, 10.5 21, 10.5 18 C10.5 15, 12 12, 16 12 Z"/>
      <path d="M16 22 L16 27 L14.5 27 L14.5 28 L17.5 28 L17.5 27 L16 27"/>
      <path d="M9 24 L23 24"/>
      <line x1="12" y1="22" x2="11" y2="24"/>
      <line x1="20" y1="22" x2="21" y2="24"/>
    </g>
  </svg>
  `,
  "uas-fixed-wing-jet": (s) => `
  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="-2 -2 36 36">
    <g transform="translate(16 16) scale(${s}) translate(-16 -16)" fill="none" stroke="#FFFFFF" stroke-width="1.0" stroke-linecap="round" stroke-linejoin="round">
      <path d="M16 4 L20.5 14 L28 17 L18.5 21 L16 27 L13.5 21 L4 17 L11.5 14 Z"/>
      <line x1="14.5" y1="19.5" x2="12.5" y2="24"/>
      <line x1="17.5" y1="19.5" x2="19.5" y2="24"/>
    </g>
  </svg>
  `,
  "uas-fixed-wing-prop": (s) => `
  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="-2 -2 36 36">
    <g transform="translate(16 16) scale(${s}) translate(-16 -16)" fill="none" stroke="#FFFFFF" stroke-width="1.0" stroke-linecap="round" stroke-linejoin="round">
      <line x1="11" y1="4" x2="21" y2="4"/>
      <line x1="16" y1="4" x2="16" y2="6"/>
      <path d="M16 6 C17 6, 17.5 8, 17.5 12 L17.5 24 L16 26 L14 24 L14 12 C14 8, 15 6, 16 6 Z"/>
      <path d="M14 11 L3 12.5 L3 14 L14 14.5 Z"/>
      <path d="M18 11 L29 12.5 L29 14 L18 14.5 Z"/>
      <path d="M10 24 L13 24 L14 23 L18 23 L19 24 L22 24 L22 26 L10 26 Z"/>
    </g>
  </svg>
  `,
  "uas-fixed-wing-generic": (s) => `
  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="-2 -2 36 36">
    <g transform="translate(16 16) scale(${s}) translate(-16 -16)" fill="none" stroke="#FFFFFF" stroke-width="1.0" stroke-linecap="round" stroke-linejoin="round">
      <path d="M16 5 L17.5 9 L17.5 24 L16 26 L14.5 24 L14.5 9 Z"/>
      <path d="M14.5 10 L4 11 L4 13 L14.5 13 Z"/>
      <path d="M17.5 10 L28 11 L28 13 L17.5 13 Z"/>
      <path d="M14.5 23 L9 24 L9 25 L16 24.5 L23 25 L23 24 L17.5 23 Z"/>
    </g>
  </svg>
  `,
  "uas-multi-rotor": (s) => `
  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="-2 -2 36 36">
    <g transform="translate(16 16) scale(${s}) translate(-16 -16)" fill="none" stroke="#FFFFFF" stroke-width="1.0" stroke-linecap="round" stroke-linejoin="round">
      <line x1="9" y1="9" x2="23" y2="23"/>
      <line x1="23" y1="9" x2="9" y2="23"/>
      <path d="M16 12 C18.2 12, 20 13.8, 20 16 C20 18.2, 18.2 20, 16 20 C13.8 20, 12 18.2, 12 16 C12 13.8, 13.8 12, 16 12 Z"/>
      <line x1="6.5" y1="9" x2="11.5" y2="9"/>
      <line x1="20.5" y1="9" x2="25.5" y2="9"/>
      <line x1="20.5" y1="23" x2="25.5" y2="23"/>
      <line x1="6.5" y1="23" x2="11.5" y2="23"/>
    </g>
  </svg>
  `,
  "uas-generic": (s) => `
  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="-2 -2 36 36">
    <g transform="translate(16 16) scale(${s}) translate(-16 -16)" fill="none" stroke="#FFFFFF" stroke-width="1.0" stroke-linecap="round" stroke-linejoin="round">
      <path d="M16 10 C19.3 10, 22 12.7, 22 16 C22 19.3, 19.3 22, 16 22 C12.7, 22, 10, 19.3, 10 16 C10 12.7, 12.7, 10, 16 10 Z"/>
      <line x1="16" y1="4" x2="16" y2="8"/>
      <line x1="16" y1="24" x2="16" y2="28"/>
      <line x1="4" y1="16" x2="8" y2="16"/>
      <line x1="24" y1="16" x2="28" y2="16"/>
    </g>
  </svg>
  `,
  usv: (s) => `
  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="-2 -2 36 36">
    <g transform="translate(16 16) scale(${s}) translate(-16 -16)" fill="none" stroke="#FFFFFF" stroke-width="1.0" stroke-linecap="round" stroke-linejoin="round">
      <path d="M16 5 L20.5 12 L20.5 24 L16 27 L11.5 24 L11.5 12 Z"/>
      <path d="M14.5 13 L17.5 13 L17.5 21 L14.5 21 Z"/>
    </g>
  </svg>
  `,
  "ship-civilian": (s) => `
  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="-2 -2 36 36">
    <g transform="translate(16 16) scale(${s}) translate(-16 -16)" fill="none" stroke="#FFFFFF" stroke-width="1.0" stroke-linecap="round" stroke-linejoin="round">
      <path d="M16 4 C19 4, 21.5 7, 21.5 11 L21.5 24 L16 27 L10.5 24 L10.5 11 C10.5 7, 13 4, 16 4 Z"/>
      <path d="M13.5 11 L18.5 11 L18.5 18 L13.5 18 Z"/>
      <line x1="13.5" y1="14.5" x2="18.5" y2="14.5"/>
    </g>
  </svg>
  `,
  "ship-military": (s) => `
  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="-2 -2 36 36">
    <g transform="translate(16 16) scale(${s}) translate(-16 -16)" fill="none" stroke="#FFFFFF" stroke-width="1.0" stroke-linecap="round" stroke-linejoin="round">
      <path d="M16 4 L21.5 11 L21.5 24 L16 27 L10.5 24 L10.5 11 Z"/>
      <line x1="12" y1="11" x2="20" y2="11"/>
      <line x1="11" y1="16" x2="21" y2="16"/>
      <line x1="11" y1="21" x2="21" y2="21"/>
    </g>
  </svg>
  `,
};

export function createAircraftMarker(category: string = "default"): string {
  // 1. Force lowercase and trim to handle input variant inconsistencies cleanly
  const sanitizedKey = category.toLowerCase().trim();

  // 2. Intercept type lookup check using 'as any' so rogue string keys can be safely evaluated
  const baseScale = (sizeScale as any)[sanitizedKey];
  const isMapped = baseScale !== undefined;

  // If unmapped, scale by 1.0 to render cleanly under the 'default' plane icon string
  const scale = isMapped ? baseScale : 1.0;

  // 3. Fall back gracefully to standard aircraft vector if key cannot be resolved inside the record
  const iconTemplate =
    (aircraftIcons as any)[sanitizedKey] || aircraftIcons["default"];

  // Quick warning to monitor dynamic tracking payloads leaking from raw WebSocket data
  if (!isMapped) {
    console.warn(
      `⚠️ Intercepted dynamic rogue icon asset key: "${category}" -> Dropping safely to default plane vector fallback.`,
    );
  }

  return iconTemplate(scale);
}
